import { useState, useEffect, useRef } from 'react'
import { Check, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useDiscoveryAssistantStore, type LocalMessage } from '@/stores/discoveryAssistantStore'

interface PollMessageProps {
  message: LocalMessage
  accountId: string
  projectId: string
  onAnswerSubmitted?: (answer: string) => void
}

export function PollMessage({ message, accountId, projectId, onAnswerSubmitted }: PollMessageProps) {
  const { pollData } = message
  if (!pollData) return null

  const submitPollAnswer = useDiscoveryAssistantStore(state => state.submitPollAnswer)

  const [selectedValues, setSelectedValues] = useState<string[]>(
    pollData.selectedValues || []
  )
  const [otherText, setOtherText] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(!!pollData.selectedValues)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const autoSubmitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Auto-submit for single-select after brief delay for visual feedback
  useEffect(() => {
    // Only auto-submit for single-select polls without "other" text input
    if (pollData.type === 'single' && selectedValues.length === 1 && !isSubmitted && !isSubmitting) {
      // Check if user selected "custom" or similar option that might need text input
      const selectedOption = pollData.options.find(o => o.id === selectedValues[0])
      const isCustomOption = selectedOption?.id === 'custom' ||
        selectedOption?.label?.toLowerCase().includes('type my own') ||
        selectedOption?.label?.toLowerCase().includes('add more details')

      if (!isCustomOption) {
        // Clear any existing timeout
        if (autoSubmitTimeoutRef.current) {
          clearTimeout(autoSubmitTimeoutRef.current)
        }

        // Auto-submit after 400ms for visual feedback
        autoSubmitTimeoutRef.current = setTimeout(() => {
          handleSubmit()
        }, 400)
      }
    }

    return () => {
      if (autoSubmitTimeoutRef.current) {
        clearTimeout(autoSubmitTimeoutRef.current)
      }
    }
  }, [selectedValues, pollData.type, isSubmitted, isSubmitting])

  const handleSubmit = async () => {
    if (isSubmitting || isSubmitted) return

    setIsSubmitting(true)

    // Build the answer text from selections
    const answerText = selectedValues.map(v => {
      const option = pollData.options.find(o => o.id === v)
      return option?.label || v
    }).join(', ') + (otherText ? `: ${otherText}` : '')

    await submitPollAnswer(
      accountId,
      projectId,
      pollData.pollId,
      selectedValues,
      otherText || undefined
    )

    setIsSubmitted(true)
    setIsSubmitting(false)

    // Trigger AI follow-up response if callback provided
    if (onAnswerSubmitted) {
      onAnswerSubmitted(answerText)
    }
  }

  const isValid = selectedValues.length > 0 || (pollData.allowOther && otherText.trim())

  return (
    <div className="flex justify-start">
      <div className="max-w-[80%] rounded-lg px-4 py-3 bg-warm-100 border border-warm-200">
        <p className="text-sm font-medium text-warm-800 mb-3">{pollData.question}</p>

        {pollData.type === 'single' ? (
          <RadioGroup
            value={selectedValues[0]}
            onValueChange={(value) => setSelectedValues([value])}
            disabled={isSubmitted}
          >
            <div className="space-y-2">
              {pollData.options.map(option => (
                <div
                  key={option.id}
                  className="flex items-start space-x-3 p-2 rounded-md hover:bg-warm-50 transition-colors"
                >
                  <RadioGroupItem value={option.id} id={option.id} />
                  <label htmlFor={option.id} className="flex-1 cursor-pointer">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-warm-900">{option.label}</span>
                      {option.isRecommended && (
                        <Badge className="text-xs bg-market-light text-white">Recommended</Badge>
                      )}
                    </div>
                    {option.description && (
                      <p className="text-xs text-text-secondary mt-1">{option.description}</p>
                    )}
                  </label>
                </div>
              ))}
            </div>
          </RadioGroup>
        ) : (
          <div className="space-y-2">
            {pollData.options.map(option => (
              <div
                key={option.id}
                className="flex items-start space-x-3 p-2 rounded-md hover:bg-warm-50 transition-colors"
              >
                <Checkbox
                  id={option.id}
                  checked={selectedValues.includes(option.id)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedValues([...selectedValues, option.id])
                    } else {
                      setSelectedValues(selectedValues.filter(v => v !== option.id))
                    }
                  }}
                  disabled={isSubmitted}
                />
                <label htmlFor={option.id} className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-warm-900">{option.label}</span>
                    {option.isRecommended && (
                      <Badge className="text-xs bg-market-light text-white">Recommended</Badge>
                    )}
                  </div>
                  {option.description && (
                    <p className="text-xs text-text-secondary mt-1">{option.description}</p>
                  )}
                </label>
              </div>
            ))}
          </div>
        )}

        {pollData.allowOther && (
          <div className="mt-3">
            <Input
              placeholder="Other (please specify)"
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              disabled={isSubmitted}
              className="text-sm"
            />
          </div>
        )}

        {/* Show submit button only for multi-select or when custom option is selected */}
        <div className="mt-4 flex justify-end">
          {isSubmitting ? (
            <div className="flex items-center gap-2 text-warm-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Processing...</span>
            </div>
          ) : isSubmitted ? (
            <div className="flex items-center gap-2 text-green-600 text-sm">
              <Check className="w-4 h-4" />
              <span>Submitted</span>
            </div>
          ) : (
            // Show button for multi-select OR when custom option is selected
            (pollData.type === 'multiple' ||
              selectedValues.includes('custom') ||
              pollData.options.find(o => selectedValues.includes(o.id))?.label?.toLowerCase().includes('type my own')) && (
              <Button
                onClick={handleSubmit}
                disabled={!isValid || isSubmitted || isSubmitting}
                size="sm"
              >
                Submit Answer
              </Button>
            )
          )}
        </div>
      </div>
    </div>
  )
}
